import { Component, OnInit, OnDestroy } from '@angular/core';
import { forkJoin, Subscription, of, Subject } from 'rxjs';

import { CalendarEvent, CalendarComponentEvent, Color, EventCategory } from '@models';
import {
  CalendarEventsService,
  DialogsService,
  ColorsService,
  EventCategoriesService,
  LoaderService,
} from '@services';
import { EventApi } from '@fullcalendar/core';
import { CalendarEventDialogComponent } from '../calendar-event-dialog/calendar-event-dialog.component';
import { EBitfPQueryComparators, EBitfPQueryOperator } from '@web/enums';
import { switchMap, takeUntil } from 'rxjs/operators';
import { IBitfApiResponse } from '@web/interfaces';

@Component({
  selector: 'prada-calendar-container',
  templateUrl: './calendar-container.component.html',
  styleUrls: ['./calendar-container.component.scss'],
})
export class CalendarContainerComponent implements OnInit, OnDestroy {
  calendarEvents: CalendarComponentEvent[] = [];
  eventCategories: EventCategory[];
  colors: Color[];
  subscription: Subscription = new Subscription();
  terminateStream$: Subject<void> = new Subject();

  constructor(
    private calendarEventsService: CalendarEventsService,
    private dialogsService: DialogsService,
    private colorsService: ColorsService,
    private eventCategoriesService: EventCategoriesService,
    private loaderService: LoaderService
  ) {}

  ngOnInit() {
    this.getCalendarEvents();
  }

  private getCalendarEvents(date: Date = new Date()) {
    this.calendarEvents = [];

    const year = date.getFullYear();
    const month = date.getMonth();
    const startDate = new Date(year, month + 1, 7);
    startDate.setHours(12);
    const endDate = new Date(year, month - 1, 25);
    endDate.setHours(12);

    this.loaderService.show();
    forkJoin({
      calendarEvents: this.calendarEventsService
        .list<CalendarEvent>({
          query: [
            {
              name: 'startTime',
              value: startDate.toISOString(),
              comparator: EBitfPQueryComparators.LOWER,
              conjunction: EBitfPQueryOperator.AND,
            },
            {
              name: 'endTime',
              value: endDate.toISOString(),
              comparator: EBitfPQueryComparators.GREATEREQUAL,
              conjunction: EBitfPQueryOperator.AND,
            },
          ],
        })
        .pipe(takeUntil(this.terminateStream$)),
      eventsCategories: of(this.eventCategories).pipe(
        switchMap(eventCategories => {
          if (eventCategories) {
            return of({ content: eventCategories } as IBitfApiResponse<EventCategory[]>);
          } else {
            return this.eventCategoriesService.list<EventCategory>({ disableHideLoader: true });
          }
        })
      ),
      colors: of(this.colors).pipe(
        switchMap(colors => {
          if (colors) {
            return of({ content: colors } as IBitfApiResponse<Color[]>);
          } else {
            return this.colorsService.list<Color>({ disableHideLoader: true });
          }
        })
      ),
    }).subscribe(({ calendarEvents, eventsCategories, colors }) => {
      this.eventCategories = eventsCategories.content;
      this.colors = colors.content;

      this.calendarEvents = calendarEvents.content
        .map((event: CalendarEvent) => event.createCalendarComponentEvent(this.eventCategories, this.colors))
        .filter((calendarComponentEvent: CalendarComponentEvent) => calendarComponentEvent.isValid());
    });
  }

  onEventClick(event: EventApi) {
    this.dialogsService.dialog.open(CalendarEventDialogComponent, {
      width: '90%',
      maxWidth: '600px',
      data: new CalendarEvent(event.extendedProps.calendarEvent),
    });
  }

  dateChange(date: Date) {
    this.terminateStream$.next();
    this.getCalendarEvents(date);
  }

  ngOnDestroy() {
    this.terminateStream$.next();
  }
}
